import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles, inputStyles } from '../styles/shared.js';
import { storeApi, resolveProductImageUrl, getProductImageDisplay, type Product, formatPrice } from '@landi/store-core';

/**
 * Product search input with autocomplete dropdown
 * 
 * @element landi-product-search
 * @fires search-submit - Fired when search is submitted
 * @fires product-select - Fired when a product is selected from dropdown
 */
@customElement('landi-product-search')
export class LandiProductSearch extends LitElement {
  static styles = [
    sharedStyles,
    inputStyles,
    css`
      :host {
        display: block;
        position: relative;
      }
      
      .search-wrapper {
        position: relative;
      }
      
      .search-input-container {
        position: relative;
        display: flex;
        align-items: center;
      }
      
      .search-icon {
        position: absolute;
        left: 1rem;
        width: 20px;
        height: 20px;
        color: var(--landi-text-light);
        pointer-events: none;
      }
      
      .search-input {
        width: 100%;
        padding: 0.875rem 1rem 0.875rem 3rem;
        font-size: 0.875rem;
        font-family: var(--landi-font-sans);
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-md);
        color: var(--landi-text);
        transition: border-color 200ms ease, box-shadow 200ms ease;
      }
      
      .search-input:focus {
        outline: none;
        border-color: var(--landi-primary);
        box-shadow: 0 0 0 3px var(--landi-primary-light);
      }
      
      .search-input::placeholder {
        color: var(--landi-text-light);
      }
      
      .clear-btn {
        position: absolute;
        right: 1rem;
        padding: 0.25rem;
        background: transparent;
        border: none;
        color: var(--landi-text-light);
        cursor: pointer;
        transition: color 200ms ease;
      }
      
      .clear-btn:hover {
        color: var(--landi-text);
      }
      
      .dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 0.5rem;
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-md);
        box-shadow: var(--landi-shadow-lg);
        z-index: 100;
        max-height: 400px;
        overflow-y: auto;
      }
      
      .dropdown-item {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.75rem 1rem;
        cursor: pointer;
        transition: background 200ms ease;
        border-bottom: 1px solid var(--landi-border-light);
      }
      
      .dropdown-item:last-child {
        border-bottom: none;
      }
      
      .dropdown-item:hover {
        background: var(--landi-background);
      }
      
      .dropdown-item:focus {
        outline: none;
        background: var(--landi-background);
      }
      
      .item-image {
        width: 48px;
        height: 48px;
        object-fit: cover;
        border-radius: var(--landi-radius);
        background: var(--landi-background);
        flex-shrink: 0;
      }
      
      .item-details {
        flex: 1;
        min-width: 0;
      }
      
      .item-name {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 0.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .item-category {
        font-size: 0.75rem;
        color: var(--landi-text-muted);
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      .item-price {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--landi-text);
        flex-shrink: 0;
      }
      
      .no-results {
        padding: 2rem 1rem;
        text-align: center;
        color: var(--landi-text-muted);
      }
      
      .search-hint {
        padding: 1rem;
        text-align: center;
        font-size: 0.813rem;
        color: var(--landi-text-light);
      }
      
      .view-all-link {
        display: block;
        padding: 0.75rem 1rem;
        text-align: center;
        font-size: 0.813rem;
        font-weight: 600;
        color: var(--landi-primary);
        background: var(--landi-background);
        border-top: 1px solid var(--landi-border);
        cursor: pointer;
        transition: color 200ms ease;
        text-decoration: none;
      }
      
      .view-all-link:hover {
        color: var(--landi-primary-hover);
      }
      
      /* Highlight matching text */
      .highlight {
        background: var(--landi-primary-light);
        color: var(--landi-primary);
        padding: 0 2px;
        border-radius: 2px;
      }
    `,
  ];

  @property({ type: String }) placeholder = 'Search products...';
  @property({ type: Number, attribute: 'min-chars' }) minChars = 2;
  @property({ type: Number, attribute: 'max-results' }) maxResults = 5;
  @property({ type: String, attribute: 'results-href' }) resultsHref = 'index.html#products';
  @property({ type: String, attribute: 'result-href-base' }) resultHrefBase = 'product.html?id=';

  @state() private query = '';
  @state() private results: Product[] = [];
  @state() private isOpen = false;
  @state() private activeIndex = -1;

  private debounceTimer?: number;

  private handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.query = input.value;
    this.activeIndex = -1;
    
    // Debounce search
    clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      this.performSearch();
    }, 200);
  }

  private async performSearch() {
    if (this.query.length >= this.minChars) {
      const results = await storeApi.searchProducts(this.query);
      this.results = results.slice(0, this.maxResults);
      this.isOpen = true;
    } else {
      this.results = [];
      this.isOpen = false;
    }
  }

  private handleFocus() {
    if (this.query.length >= this.minChars) {
      this.isOpen = true;
    }
  }

  private handleBlur() {
    // Delay to allow click on dropdown items
    setTimeout(() => {
      this.isOpen = false;
    }, 200);
  }

  private handleKeydown(e: KeyboardEvent) {
    if (!this.isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (this.activeIndex >= 0 && this.results[this.activeIndex]) {
          this.selectProduct(this.results[this.activeIndex]);
        } else {
          this.submitSearch();
        }
        break;
      case 'Escape':
        this.isOpen = false;
        break;
    }
  }

  private selectProduct(product: Product) {
    this.dispatchEvent(new CustomEvent('product-select', {
      bubbles: true,
      composed: true,
      detail: { product },
    }));
    
    // Navigate to product page using configurable href base
    window.location.href = `${this.resultHrefBase}${product.id}`;
  }

  private submitSearch() {
    this.dispatchEvent(new CustomEvent('search-submit', {
      bubbles: true,
      composed: true,
      detail: { query: this.query },
    }));
    
    // Navigate to search results
    window.location.href = `${this.resultsHref}?q=${encodeURIComponent(this.query)}`;
  }

  private clearSearch() {
    this.query = '';
    this.results = [];
    this.isOpen = false;
    this.shadowRoot?.querySelector<HTMLInputElement>('.search-input')?.focus();
  }

  private highlightMatch(text: string, query: string): string {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  render() {
    return html`
      <div class="search-wrapper">
        <div class="search-input-container">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          
          <input
            type="text"
            class="search-input"
            .value=${this.query}
            placeholder=${this.placeholder}
            @input=${this.handleInput}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
            @keydown=${this.handleKeydown}
            aria-label="Search products"
            aria-autocomplete="list"
            aria-expanded=${this.isOpen}
          />
          
          ${this.query ? html`
            <button 
              class="clear-btn" 
              @click=${this.clearSearch}
              aria-label="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          ` : ''}
        </div>
        
        ${this.isOpen ? html`
          <div class="dropdown" role="listbox">
            ${this.results.length > 0 ? html`
              ${this.results.map((product, index) => {
                const imageDisplay = getProductImageDisplay({
                  url: resolveProductImageUrl(product.images[0]?.url),
                  name: product.name,
                  sku: product.sku,
                  category: product.category,
                  theme: 'light',
                });
                return html`
                <div 
                  class="dropdown-item"
                  role="option"
                  tabindex="0"
                  aria-selected=${index === this.activeIndex}
                  @click=${() => this.selectProduct(product)}
                  @mouseenter=${() => this.activeIndex = index}
                >
                  <img 
                    class="item-image" 
                    src=${imageDisplay.src}
                    alt=""
                    .onerror=${imageDisplay.onError}
                  />
                  <div class="item-details">
                    <p class="item-name" .innerHTML=${this.highlightMatch(product.name, this.query)}></p>
                    <p class="item-category">${product.category || 'Product'}</p>
                  </div>
                  <span class="item-price">${formatPrice(product.price)}</span>
                </div>
              `;
              })}
              
              <a href="${this.resultsHref}?q=${encodeURIComponent(this.query)}" class="view-all-link">
                View all results for "${this.query}"
              </a>
            ` : this.query.length >= this.minChars ? html`
              <div class="no-results">
                <p>No products found for "${this.query}"</p>
              </div>
            ` : html`
              <div class="search-hint">
                <p>Type at least ${this.minChars} characters to search</p>
              </div>
            `}
          </div>
        ` : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-search': LandiProductSearch;
  }
}
